import React from "react";
import { GripVertical } from "lucide-react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Slide } from "@/common/decks";

interface DraggableSlidesListProps {
  slides: Slide[];
  onReorder: (fromIndex: number, toIndex: number) => void;
}

interface SortableSlideItemProps {
  slide: Slide;
}

const SortableSlideItem: React.FC<SortableSlideItemProps> = ({ slide }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: slide.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-x-2 p-2 rounded-md transition-all duration-200 relative cursor-grab active:cursor-grabbing ${
        isDragging
          ? "opacity-50 scale-105 bg-deck-primary/20 shadow-lg z-10"
          : "bg-deck-400 hover:bg-deck-primary/5"
      }`}
      {...attributes}
      {...listeners}
    >
      <GripVertical
        className={`transition-colors duration-200 ${
          isDragging ? "text-deck-primary" : "text-deck-text"
        }`}
        size={20}
      />
      <span className="text-deck-primary font-medium truncate">
        {slide.title}
      </span>
    </div>
  );
};

const DraggableSlidesList: React.FC<DraggableSlidesListProps> = ({
  slides,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = slides.findIndex((slide) => slide.id === active.id);
      const newIndex = slides.findIndex((slide) => slide.id === over.id);

      onReorder(oldIndex, newIndex);
    }
  };

  return (
    <div className="w-72">
      <span className="block font-bold text-deck-text mb-3">
        Reorder Slides
      </span>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={slides.map((slide) => slide.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-y-2">
            {slides.map((slide) => (
              <SortableSlideItem key={slide.id} slide={slide} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default DraggableSlidesList;
